import Primarytheme from './theme';
import React, { useState, useRef } from 'react';

import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';

import Tooltip from "@mui/material/Tooltip";
import HelpIcon from '@mui/icons-material/Help';

import FileUploadIcon from '@mui/icons-material/FileUpload';
import CloseIcon from '@mui/icons-material/Close';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FontDownloadIcon from '@mui/icons-material/FontDownload';
import ImageIcon from '@mui/icons-material/Image';
import SvgIcon from '@mui/material/SvgIcon';
import { ReactComponent as IndesignLogo } from '../../logos/indesign_black.svg';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { getContentType, encodeTag, supportedLinksFormats } from '../helpers/assets';

import { getSessionIdToken } from '../auth/apiAuth'

import axios from 'axios';

const {
    REACT_APP_USER_ID,
} = process.env;

const FONT_EXTENSIONS = ['.otf', '.ttf'];

function categorizeFile(file, format) {
    const name = file.name.toLowerCase();
    const path = file.relativePath || file.webkitRelativePath || '';
    const pathLower = path.toLowerCase();

    if (name.endsWith(`.${format.ext}`)) return 'inDesign';
    if (name.endsWith('.pdf')) return 'Pdf';

    if (pathLower.includes('document fonts/') || pathLower.includes('document fonts\\')) return 'Document fonts';
    if (FONT_EXTENSIONS.some(ext => name.endsWith(ext))) return 'Document fonts';

    if (pathLower.includes('links/') || pathLower.includes('links\\')) return 'Links';
    if (supportedLinksFormats.some(ext => name.endsWith(ext))) return 'Links';

    return null;
}

async function getFilesFromDataTransfer(dataTransfer) {
    const files = [];
    const readEntry = (entry, parentPath = '') => {
        return new Promise((resolve) => {
            if (entry.isFile) {
                entry.file(f => {
                    Object.defineProperty(f, 'relativePath', { value: parentPath + f.name, writable: false });
                    files.push(f);
                    resolve();
                });
            } else if (entry.isDirectory) {
                const reader = entry.createReader();
                const dirPath = parentPath + entry.name + '/';
                const readAll = (entries = []) => {
                    reader.readEntries(async (batch) => {
                        if (batch.length === 0) {
                            await Promise.all(entries.map(e => readEntry(e, dirPath)));
                            resolve();
                        } else {
                            readAll([...entries, ...batch]);
                        }
                    });
                };
                readAll();
            } else {
                resolve();
            }
        });
    };
    const items = dataTransfer.items;
    const entries = [];
    for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry && items[i].webkitGetAsEntry();
        if (entry) entries.push(entry);
    }
    await Promise.all(entries.map(e => readEntry(e, '')));
    return files;
}

export default function GenericMultiUpload({ auth, bucket, project_id, folderObj, folderName, format = { ext: 'idml', version: 'CC 2020' }, refreshTable, uploadTags, closeDialog, disabled, action, checkLinks, setLinks, onUploadingChange }) {

    const [openAsset, setOpenAsset] = useState({ open: false, message: '' });
    const [selectedAssets, setSelectedAssets] = useState({});
    const [assetProgressGlobal, setAssetProgressGlobal] = useState(0);
    const [isLoadingAsset, setIsLoadingAsset] = useState({});
    const [isLoadingAssetGlobal, setIsLoadingAssetGlobal] = useState(false);
    const [showWarningInddFormat, setShowWarningInddFormat] = useState('');
    const [showUploadMessage, setShowUploadMessage] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [uncategorizedFiles, setUncategorizedFiles] = useState([]);
    const [duplicateDialog, setDuplicateDialog] = useState({ open: false, docPath: null });
    const [isCheckingTags, setIsCheckingTags] = useState(false);
    const abortRef = useRef(false);
    const activeRequestsRef = useRef([]);

    const setUploading = (value) => {
        setIsLoadingAssetGlobal(value);
        if (onUploadingChange) onUploadingChange(value);
    };

    const updateObjState = (stateObj, setStateObj, value, folder) => {
        setStateObj(stateObj => {
            const updatedStateObj = { ...stateObj };
            updatedStateObj[folder] = value;
            return updatedStateObj;
        })
    }

    const handleCloseAsset = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenAsset({ open: false, message: '' });
    };

    const handleClearAssetsAll = (folder) => {
        updateObjState(selectedAssets, setSelectedAssets, [], folder);
        updateObjState(isLoadingAsset, setIsLoadingAsset, false, folder);
        if (showWarningInddFormat) setShowWarningInddFormat('')
    };

    const handleClearAll = () => {
        setSelectedAssets({});
        setUncategorizedFiles([]);
        if (showWarningInddFormat) setShowWarningInddFormat('');
    };

    const processFiles = (files) => {
        const categorized = {};
        const uncategorized = [];

        for (const file of files) {
            const category = categorizeFile(file, format);
            if (category) {
                if (!categorized[category]) categorized[category] = [];
                categorized[category].push(file);
            } else {
                uncategorized.push(file);
            }
        }

        if (categorized['inDesign'] && categorized['inDesign'].length > 0) {
            const inddFile = categorized['inDesign'][0];
            if (format.ext === 'idml' && !inddFile.name.toLowerCase().endsWith('.idml')) {
                setShowWarningInddFormat('The file is not a valid .idml document');
                delete categorized['inDesign'];
            } else if (format.ext === 'indd' && !inddFile.name.toLowerCase().endsWith('.indd')) {
                setShowWarningInddFormat('The file is not a valid .indd document');
                delete categorized['inDesign'];
            } else {
                if (showWarningInddFormat) setShowWarningInddFormat('');
            }
        }

        setSelectedAssets(prev => {
            const updated = { ...prev };
            for (const [cat, catFiles] of Object.entries(categorized)) {
                const existing = updated[cat] ? Array.from(updated[cat]) : [];
                const existingNames = new Set(existing.map(f => f.name));
                updated[cat] = [...existing, ...catFiles.filter(f => !existingNames.has(f.name))];
            }
            return updated;
        });

        if (uncategorized.length > 0) {
            setUncategorizedFiles(prev => [...prev, ...uncategorized]);
        }
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (isLoadingAssetGlobal) return;

        if (disabled) {
            setOpenAsset({ open: true, message: 'Please fill in all tag fields before uploading files' });
            return;
        }

        const files = await getFilesFromDataTransfer(e.dataTransfer);
        if (files.length > 0) {
            processFiles(files);
        }
    };

    const handleFileInputChange = (event, folder) => {
        const files = Array.from(event.target.files);
        if (folder) {
            files.forEach(f => {
                Object.defineProperty(f, 'relativePath', { value: folder + '/' + f.name, writable: false });
            });
        }
        processFiles(files);
    };

    const checkIdmlFiles = async (docPath) => {
        //console.log('init checks ...')
        return axios({
            method: "post",
            url: `${process.env.REACT_APP_API_BASE_URL}/assets`,
            headers: {
                'user_id': REACT_APP_USER_ID || auth.ssmParams.USER_ID,
                "content-type": "application/json",
                "Authorization": await getSessionIdToken(),
                "src": docPath,
                "project_id": project_id,
                "action": 'dynamo-checks'
            },
        })
            .then(resp => {
                const linksObj = {
                    missingLiks: resp.data.linksCheckArr,
                    unusedLinks: resp.data.unusedLinksArr,
                }
                setLinks(linksObj);
                return resp;
            })
            .catch(err => {
                return err;
            })
    }

    const handleCancelUpload = async () => {
        abortRef.current = true;
        activeRequestsRef.current.forEach(req => {
            try { req.abort(); } catch (e) { /* ignore */ }
        });
        activeRequestsRef.current = [];
        setUploading(false);
        setShowUploadMessage(false);
        setAssetProgressGlobal(0);

        if (action !== 'update') {
            try {
                await axios({
                    method: "post",
                    url: `${process.env.REACT_APP_API_BASE_URL}/assets`,
                    headers: {
                        'user_id': REACT_APP_USER_ID || auth.ssmParams.USER_ID,
                        "content-type": "application/json",
                        "Authorization": await getSessionIdToken(),
                        "action": "delete-indesign"
                    },
                    data: {
                        docPath: folderObj['inDesign']
                    }
                });
            } catch (e) {
                console.error('rollback delete failed');
            }
        }

        setOpenAsset({ open: true, message: 'Upload cancelled' });
    };

    const checkTagsExists = async () => {
        const tags = uploadTags.map(tagObj => ({ Key: encodeTag(tagObj.name), Value: encodeTag(tagObj.value) }));
        try {
            const resp = await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_BASE_URL}/assets`,
                headers: {
                    'user_id': REACT_APP_USER_ID || auth.ssmParams.USER_ID,
                    "content-type": "application/json",
                    "Authorization": await getSessionIdToken(),
                    "action": "check-tags-exists"
                },
                data: { project_id, selectedFolder: folderName, tags }
            });
            return resp.data;
        } catch (e) {
            console.error('check-tags-exists failed');
            return { exists: false };
        }
    };

    const handleUploadClick = async () => {
        if (action === 'update') {
            handleUploadDocument();
            return;
        }
        setIsCheckingTags(true);
        const result = await checkTagsExists();
        setIsCheckingTags(false);
        if (result.exists) {
            setDuplicateDialog({ open: true, docPath: result.docPath });
        } else {
            handleUploadDocument();
        }
    };

    const handleConfirmReplace = async () => {
        const { docPath } = duplicateDialog;
        setDuplicateDialog({ open: false, docPath: null });
        try {
            await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_BASE_URL}/assets`,
                headers: {
                    'user_id': REACT_APP_USER_ID || auth.ssmParams.USER_ID,
                    "content-type": "application/json",
                    "Authorization": await getSessionIdToken(),
                    "action": "delete-indesign"
                },
                data: { docPath }
            });
        } catch (e) {
            console.error('delete existing package failed');
        }
        handleUploadDocument();
    };

    const handleUploadDocument = async () => {
        /* console.log('folderObj', folderObj)
        console.log('selectedAssets', selectedAssets) */
        setUploading(true);
        abortRef.current = false;
        try {
            const folderToUpload = Object.keys(selectedAssets);

            setShowUploadMessage(true);

            const tagsEncoded = uploadTags.map(k => `${encodeURIComponent(encodeTag(k.name))}=${encodeURIComponent(encodeTag(k.value))}`).join('&');
            const uriEncodedTags = uploadTags.map(tagObj => { return { Key: encodeURIComponent(encodeTag(tagObj.name)), Value: encodeURIComponent(encodeTag(tagObj.value)) } })

            //console.log('handle upload tags', tagsEncoded)
            const sizing = { total: 0, uploaded: [] };
            const filesToUpload = [];
            for (const f of folderToUpload) {
                sizing.total += Array.from(selectedAssets[f]).reduce((a, b) => +a + +b.size, 0);
                sizing.uploaded.push(...Array(selectedAssets[f].length).fill(0))
                for (const sf of selectedAssets[f]) {
                    sf.dirName = folderObj[f].replace(bucket + '/', '');
                }
                filesToUpload.push(...selectedAssets[f])
            }

            const promises = [];
            let dynamoArr = [];
            const failedFiles = [];
            activeRequestsRef.current = [];
            for (const [id, f] of filesToUpload.entries()) {
                if (abortRef.current) break;

                promises.push(
                    new Promise((resolve, reject) => {
                        const request = auth.s3.upload({
                            Body: f,
                            Bucket: bucket,
                            Key: f.dirName + '/' + f.name,
                            Tagging: tagsEncoded,
                            ContentType: getContentType(f.name.split('.').pop())
                        });
                        activeRequestsRef.current.push(request);
                        request
                            .on('httpUploadProgress', evt => {
                                sizing.uploaded[id] = evt.loaded;
                                setAssetProgressGlobal(sizing.uploaded.reduce((partialSum, a) => partialSum + a, 0) / sizing.total * 100);
                            })
                            .send((err, data) => {
                                if (err) {
                                    if (abortRef.current) return resolve();
                                    console.error('error uploading data, please refresh')
                                    return reject(f.name);
                                } else {
                                    dynamoArr.push({
                                        ...data,
                                        uploadDate: new Date().getTime(),
                                        name: f.name,
                                        lastModified: f.lastModified,
                                        lastModifiedDate: f.lastModifiedDate,
                                        size: f.size,
                                        type: f.type ? f.type : 'unknown',
                                        selectedFolder: folderName,
                                        fileCategory: folderToUpload.indexOf(f.dirName.split('/').pop()) !== -1 ? f.dirName.split('/').pop() : 'inDesign',
                                        tags: JSON.stringify(uriEncodedTags),
                                        project_id: project_id,
                                        reqId: folderObj['inDesign'].split('/').pop()
                                    })
                                    return resolve();
                                }
                            })
                    })
                )

                if (id % 25 === 0 || id === filesToUpload.length - 1) {
                    const results = await Promise.allSettled(promises);
                    results.filter(r => r.status === 'rejected').forEach(r => failedFiles.push(r.reason));
                    promises.length = 0

                    if (abortRef.current) break;

                    let dynamoArrRequest = [];
                    for (let i = 0; i < dynamoArr.length; i++) {
                        dynamoArrRequest.push(dynamoArr[i]);

                        if (i % 25 === 0 || i === dynamoArr.length - 1) {
                            const putReq = await axios({
                                method: "post",
                                url: `${process.env.REACT_APP_API_BASE_URL}/assets`,
                                headers: {
                                    'user_id': REACT_APP_USER_ID || auth.ssmParams.USER_ID,
                                    "content-type": "application/json",
                                    "Authorization": await getSessionIdToken(),
                                    "action": 'new-item-dynamo-batch',
                                },
                                data: JSON.stringify(dynamoArrRequest)
                            })

                            dynamoArrRequest = [];
                        }
                    }
                    dynamoArr = [];
                }
            }

            if (abortRef.current) return;

            if (checkLinks !== 'false' && checkLinks !== false) {
                const checkDone = await checkIdmlFiles(folderObj['inDesign'])
            }

            if (failedFiles.length > 0) {
                const fileList = failedFiles.length <= 5
                    ? failedFiles.join(', ')
                    : failedFiles.slice(0, 5).join(', ') + ` and ${failedFiles.length - 5} more`;
                setShowUploadMessage(true);
                setOpenAsset({ open: true, message: `Upload failed for ${failedFiles.length} file(s): ${fileList}`, duration: null });
            }

            closeDialog()
            refreshTable()
        } catch (err) {
            console.error('Error during upload process');
            setOpenAsset({ open: true, message: 'Upload process failed unexpectedly', duration: null });
        } finally {
            setUploading(false);
        }
    }

    const totalFilesCount = Object.values(selectedAssets).reduce((sum, files) => sum + (files ? files.length : 0), 0);

    const getCategoryIcon = (category, size = 16) => {
        switch (category) {
            case 'inDesign': return <SvgIcon component={IndesignLogo} viewBox="0 0 24 24" sx={{ fontSize: size, color: 'primary.main' }} />;
            case 'Document fonts': return <FontDownloadIcon sx={{ fontSize: size, color: 'primary.main' }} />;
            case 'Links': return <ImageIcon sx={{ fontSize: size, color: 'primary.main' }} />;
            case 'Pdf': return <InsertDriveFileIcon sx={{ fontSize: size, color: 'error.main' }} />;
            default: return <InsertDriveFileIcon sx={{ fontSize: size }} />;
        }
    };

    const getCategoryLabel = (category) => {
        if (category === 'inDesign') {
            return format.ext === 'indd' ? `InDesign INDD - ${format.version}` : 'InDesign IDML';
        }
        return category;
    };

    return (
        <Box>
            {action !== 'update' && (
                <Box mt='1em' mb='2em'>
                    {/* Drag & drop area */}
                    <Box
                        onDragOver={(e) => { e.preventDefault(); if (!disabled && !isLoadingAssetGlobal) setIsDragOver(true); }}
                        onDragLeave={() => setIsDragOver(false)}
                        onDrop={handleDrop}
                        sx={{
                            width: '100%',
                            border: isDragOver ? '2px dashed' : '2px dashed #ccc',
                            borderColor: isDragOver ? 'primary.main' : (disabled ? 'grey.300' : '#ccc'),
                            borderRadius: 2,
                            px: 3,
                            py: 4,
                            textAlign: 'center',
                            backgroundColor: isDragOver ? 'rgba(66,135,245,0.06)' : 'transparent',
                            cursor: disabled ? 'default' : 'pointer',
                            transition: 'all 0.2s',
                            opacity: disabled ? 0.4 : 1,
                            '&:hover': disabled ? {} : { borderColor: 'primary.main', backgroundColor: 'rgba(66,135,245,0.03)' },
                        }}
                        onClick={() => {
                            if (disabled || isLoadingAssetGlobal) return;
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.multiple = true;
                            input.webkitdirectory = true;
                            input.onchange = (event) => handleFileInputChange(event, null);
                            input.click();
                        }}
                    >
                        <Stack direction='column' spacing={1} justifyContent='center' alignItems='center'>
                            <FolderIcon color={isDragOver ? 'primary' : 'disabled'} sx={{ fontSize: 48 }} />
                            <Typography variant="body2" color={isDragOver ? 'primary' : 'text.secondary'} fontWeight="medium">
                                {isDragOver ? 'Drop package folder here' : 'Drag & drop an InDesign Package folder here'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Expected: .{format.ext} file, Links folder (images), Document fonts folder (.otf, .ttf)
                            </Typography>
                        </Stack>
                    </Box>

                    {/* Manual selection buttons */}
                    <Stack direction="row" spacing={1} mt={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                        {Object.keys(folderObj).filter(f => !(f === 'Pdf' && format.ext === 'idml')).map(folder => {
                            let exts;
                            if (folder === 'inDesign') exts = format.ext === 'indd' ? '.indd' : '.idml';
                            else if (folder === 'Links') exts = supportedLinksFormats.join(',');
                            else if (folder === 'Document fonts') exts = '.otf,.ttf';
                            else if (folder === 'Pdf') exts = '.pdf';

                            return (
                                <Button
                                    key={folder}
                                    variant="outlined"
                                    component="label"
                                    size="small"
                                    disabled={disabled || isLoadingAssetGlobal}
                                    startIcon={getCategoryIcon(folder)}
                                >
                                    {getCategoryLabel(folder)}
                                    <input
                                        type="file"
                                        multiple={folder !== 'inDesign'}
                                        hidden
                                        accept={exts}
                                        onChange={(event) => handleFileInputChange(event, folder)}
                                        onClick={(event) => { event.target.value = null; }}
                                    />
                                </Button>
                            );
                        })}
                    </Stack>

                    {/* Warning for wrong indd format */}
                    {showWarningInddFormat && (
                        <Alert elevation={0} severity="warning" sx={{ mt: 1.5 }}>{showWarningInddFormat}</Alert>
                    )}

                    {/* Categorized file list */}
                    {totalFilesCount > 0 && (
                        <Box sx={{ mt: 2, border: '1px solid #eee', borderRadius: 1, p: 1.5 }}>
                            {uncategorizedFiles.length > 0 && (
                                <Alert severity="info" sx={{ mb: 1.5 }}>
                                    {uncategorizedFiles.length} file(s) were skipped (unrecognized type): {uncategorizedFiles.map(f => f.name).join(', ')}
                                </Alert>
                            )}

                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                                <Typography variant="subtitle2">{totalFilesCount} file(s) ready to upload</Typography>
                                <Button size="small" onClick={handleClearAll} startIcon={<CloseIcon fontSize="small" />}>
                                    Clear all
                                </Button>
                            </Stack>

                            <Stack direction="row" justifyContent="space-evenly" alignItems="flex-start" divider={<Box sx={{ width: '1px', backgroundColor: '#e0e0e0', alignSelf: 'stretch' }} />}>
                                {Object.entries(selectedAssets).filter(([, files]) => files && files.length > 0).map(([category, files]) => (
                                    <Box key={category} sx={{ flex: 1, minWidth: 0, px: 1.5 }}>
                                        <Stack direction="column" alignItems="center" spacing={0.5} mb={0.5}>
                                            {getCategoryIcon(category, 28)}
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <Typography variant="caption" fontWeight="bold">{getCategoryLabel(category)}</Typography>
                                                <Chip label={files.length} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                                                <IconButton size="small" onClick={() => handleClearAssetsAll(category)}>
                                                    <CloseIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Stack>
                                        </Stack>
                                        <Box sx={{ maxHeight: 120, overflowY: 'auto' }}>
                                            {Array.from(files).map((file, idx) => (
                                                <Typography key={idx} variant="caption" display="block" color="text.secondary" noWrap>
                                                    {file.name}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Box>
            )}

            {action === 'update' && (
                <>
                    <Box id='inDesignUpdate'>
                        <Typography component='div' variant='body' color='textSecondary' mt='1.5em'>
                            Select the {format.ext} file that will replace the existing one.
                        </Typography>
                        <Box mt='1em' mb='2em'>
                            <Box
                                onDragOver={(e) => { e.preventDefault(); if (!disabled && !isLoadingAssetGlobal) setIsDragOver(true); }}
                                onDragLeave={() => setIsDragOver(false)}
                                onDrop={handleDrop}
                                sx={{
                                    width: '100%',
                                    border: isDragOver ? '2px dashed' : '2px dashed #ccc',
                                    borderColor: isDragOver ? 'primary.main' : (disabled ? 'grey.300' : '#ccc'),
                                    borderRadius: 2,
                                    px: 3,
                                    py: 3,
                                    textAlign: 'center',
                                    backgroundColor: isDragOver ? 'rgba(66,135,245,0.06)' : 'transparent',
                                    cursor: disabled ? 'default' : 'pointer',
                                    transition: 'all 0.2s',
                                    opacity: disabled ? 0.4 : 1,
                                    '&:hover': disabled ? {} : { borderColor: 'primary.main', backgroundColor: 'rgba(66,135,245,0.03)' },
                                }}
                                onClick={() => {
                                    if (disabled || isLoadingAssetGlobal) return;
                                    const input = document.createElement('input');
                                    input.type = 'file';
                                    input.multiple = true;
                                    input.webkitdirectory = true;
                                    input.onchange = (event) => handleFileInputChange(event, null);
                                    input.click();
                                }}
                            >
                                <Stack direction='column' spacing={0.5} justifyContent='center' alignItems='center'>
                                    <FolderIcon color={isDragOver ? 'primary' : 'disabled'} sx={{ fontSize: 40 }} />
                                    <Typography variant="body2" color={isDragOver ? 'primary' : 'text.secondary'}>
                                        {isDragOver ? 'Drop files here' : 'Drag & drop files or a package folder here'}
                                    </Typography>
                                </Stack>
                            </Box>

                            <Stack direction="row" spacing={1} mt={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                                {Object.keys(folderObj).map(folder => {
                                    let exts;
                                    if (folder === 'inDesign') exts = format.ext === 'indd' ? '.indd' : '.idml';
                                    else if (folder === 'Links') exts = supportedLinksFormats.join(',');
                                    else if (folder === 'Document fonts') exts = '.otf,.ttf';
                                    else if (folder === 'Pdf') exts = '.pdf';

                                    return (
                                        <Button
                                            key={folder}
                                            variant="outlined"
                                            component="label"
                                            size="small"
                                            disabled={disabled || isLoadingAssetGlobal}
                                            startIcon={getCategoryIcon(folder)}
                                        >
                                            {getCategoryLabel(folder)}
                                            <input
                                                type="file"
                                                multiple={folder !== 'inDesign'}
                                                hidden
                                                accept={exts}
                                                onChange={(event) => handleFileInputChange(event, folder)}
                                                onClick={(event) => { event.target.value = null; }}
                                            />
                                        </Button>
                                    );
                                })}
                            </Stack>

                            {showWarningInddFormat && (
                                <Alert elevation={0} severity="warning" sx={{ mt: 1.5 }}>{showWarningInddFormat}</Alert>
                            )}

                            {totalFilesCount > 0 && (
                                <Box sx={{ mt: 2, border: '1px solid #eee', borderRadius: 1, p: 1.5 }}>
                                    {uncategorizedFiles.length > 0 && (
                                        <Alert severity="info" sx={{ mb: 1.5 }}>
                                            {uncategorizedFiles.length} file(s) were skipped (unrecognized type): {uncategorizedFiles.map(f => f.name).join(', ')}
                                        </Alert>
                                    )}

                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                                        <Typography variant="subtitle2">{totalFilesCount} file(s) ready to upload</Typography>
                                        <Button size="small" onClick={handleClearAll} startIcon={<CloseIcon fontSize="small" />}>
                                            Clear all
                                        </Button>
                                    </Stack>

                                    <Stack direction="row" spacing={2} justifyContent="space-evenly" alignItems="flex-start">
                                        {Object.entries(selectedAssets).filter(([, files]) => files && files.length > 0).map(([category, files]) => (
                                            <Box key={category} sx={{ flex: 1, minWidth: 0 }}>
                                                <Stack direction="column" alignItems="center" spacing={0.5} mb={0.5}>
                                                    {getCategoryIcon(category, 28)}
                                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                                        <Typography variant="caption" fontWeight="bold">{getCategoryLabel(category)}</Typography>
                                                        <Chip label={files.length} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                                                        <IconButton size="small" onClick={() => handleClearAssetsAll(category)}>
                                                            <CloseIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    </Stack>
                                                </Stack>
                                                <Box sx={{ maxHeight: 120, overflowY: 'auto' }}>
                                                    {Array.from(files).map((file, idx) => (
                                                        <Typography key={idx} variant="caption" display="block" color="text.secondary" noWrap sx={{ textAlign: 'center' }}>
                                                            {file.name}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </>
            )}

            <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
                <Button
                    variant="contained"
                    color='secondary'
                    size='medium'
                    loading={isLoadingAssetGlobal || isCheckingTags}
                    loadingIndicator={(
                        <Stack direction='row' alignItems='center'>
                            <CircularProgress color="inherit" size={16} variant={isCheckingTags ? 'indeterminate' : 'determinate'} value={assetProgressGlobal} />
                            {!isCheckingTags && <Typography variant='body2' ml='0.5em'>{assetProgressGlobal.toFixed(0)}%</Typography>}
                        </Stack>
                    )}
                    disabled={disabled || isCheckingTags ||
                        (action !== 'update' && (!selectedAssets['inDesign'] || (selectedAssets['inDesign'] && selectedAssets['inDesign'].length === 0))) ||
                        (action === 'update' && Object.keys(selectedAssets).length === 0)}
                    onClick={handleUploadClick}
                >
                    {action !== 'update' ? 'UPLOAD' : 'UPLOAD AND CHECK'}
                </Button>
                {isLoadingAssetGlobal && (
                    <Button
                        variant="outlined"
                        color="error"
                        size="medium"
                        onClick={handleCancelUpload}
                    >
                        CANCEL
                    </Button>
                )}
            </Stack>
            {showUploadMessage && (
                <Typography component='div' variant='caption' color='error' sx={{ padding: '0.8em', textAlign: 'center' }}>
                    The uploading process could take a few minutes. Please wait and do not close the window until it finishes.
                </Typography>
            )}

            <Dialog open={duplicateDialog.open} onClose={() => setDuplicateDialog({ open: false, docPath: null })}>
                <DialogTitle>Package already exists</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        A package with this tag combination already exists. Uploading will replace the existing package and all its files.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDuplicateDialog({ open: false, docPath: null })}>Cancel</Button>
                    <Button onClick={handleConfirmReplace} variant="contained" color="primary">Replace</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                open={openAsset.open}
                autoHideDuration={6000}
                onClose={handleCloseAsset}
                message={openAsset.message}
                action={
                    <React.Fragment>
                        <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseAsset}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </React.Fragment>
                }
            />
        </Box>
    )
}
